import {
  Box,
  Button,
  Chip,
  Divider,
  Theme,
  Typography,
  useTheme
} from "@outschool/backpack";
import { SUBSCRIPTION_PLANS } from "@outschool/business-rules";
import {
  ResumeSubscriptionMutation,
  ResumeSubscriptionMutationVariables
} from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { Trans, useTranslation } from "@outschool/localization";
import { homePath, joinMembershipPath, searchPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  MembershipOptionCard,
  useConfetti,
  useLoggedOutMembershipsExperiment
} from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useNavigation } from "@outschool/ui-utils";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from "@stripe/react-stripe-js";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import * as Env from "../../../shared/Env";
import { useSubscriptionCreditBalance } from "../../hooks/useSubscriptionCreditBalance";
import PageModal from "../PageModal";
import { resumeSubscriptionMutation } from "../subscriptions/PauseMembershipModal";
import OrangeFingerprintsBottomRight from "./images/orange_fingerprints_bottom_right.png";
import PurpleLinesTopLeft from "./images/purple_lines_top_left.png";
import { stripePromise } from "./Stripe";

enum STATUS {
  SELECT_PLAN,
  RESUME_PLAN,
  LOADING_CLIENT_SECRET,
  EMBEDDED_CHECKOUT,
  PROVISIONING_SUBSCRIPTION,
  PROVISIONING_SUCCESS,
  POLLING_TIMEOUT,
  PAYMENT_FAILED
}

type JoinMembershipModalStateType = {
  status: STATUS;
  clientSecret: string | null;
  selectedPlan: string | null;
  isModalOpen: boolean;
  pollForSubscription: boolean;
};

const initialState: JoinMembershipModalStateType = {
  status: STATUS.SELECT_PLAN,
  clientSecret: null,
  selectedPlan: null,
  isModalOpen: false,
  pollForSubscription: false
};

function reducer(
  state: JoinMembershipModalStateType,
  action: {
    type: string;
    selectedPlan?: string | null | undefined;
    clientSecret?: string | null | undefined;
    isModalOpen?: boolean;
    pollForSubscription?: boolean;
  }
) {
  switch (action.type) {
    case "OPEN_MODAL":
      return { ...state, isModalOpen: true };
    case "CLOSE_MODAL":
    case "RESET":
      return initialState;
    case "SET_PLAN":
      return { ...state, selectedPlan: action.selectedPlan };
    case "RESUME_PLAN":
      return { ...state, status: STATUS.RESUME_PLAN };
    case "LOADING_CLIENT_SECRET":
      return { ...state, status: STATUS.LOADING_CLIENT_SECRET };
    case "SET_CLIENT_SECRET":
      return {
        ...state,
        clientSecret: action.clientSecret,
        status: STATUS.EMBEDDED_CHECKOUT
      };
    case "PROVISIONING_SUBSCRIPTION":
      return {
        ...state,
        status: STATUS.PROVISIONING_SUBSCRIPTION,
        pollForSubscription: true
      };
    case "PROVISIONING_SUCCESS":
      return {
        ...state,
        status: STATUS.PROVISIONING_SUCCESS,
        pollForSubscription: false
      };
    case "POLLING_TIMEOUT":
      return { ...state, status: STATUS.POLLING_TIMEOUT };
    case "PAYMENT_FAILED":
      return {
        ...state,
        clientSecret: null,
        selectedPlan: null,
        status: STATUS.PAYMENT_FAILED
      };
    default:
      return state;
  }
}

const MembershipResumeContent = ({
  afterResume
}: {
  afterResume: () => void;
}) => {
  const { t } = useTranslation("client\\components\\payments\\Stripe");
  const trackEvent = useTrackEvent();
  const { willResumeAt } = useSubscriptionCreditBalance();

  const [resumeSubscription] = useMutation<
    ResumeSubscriptionMutation,
    ResumeSubscriptionMutationVariables
  >(resumeSubscriptionMutation);

  return (
    <Box
      sx={{
        paddingRight: "2em"
      }}
    >
      <Typography variant="h3" sx={{ paddingRight: "2em" }}>
        {t("Resume Your Membership")}
      </Typography>
      <Box
        sx={{
          marginY: 24
        }}
      >
        <Typography variant="body1" sx={{ display: "block" }}>
          {t("Your membership is set to resume on {{resumeDate}}.", {
            resumeDate: dayjs(willResumeAt).format("MM/DD/YYYY")
          })}
        </Typography>
        <Typography variant="body1" sx={{ marginTop: 24, display: "block" }}>
          {t(
            "You can resume at any time to regain access to your credits and membership perks. You can also update your pause period here."
          )}
        </Typography>
      </Box>
      <Box>
        <Button
          variant="contained"
          onClick={() => {
            trackEvent("JOIN_MEMBERSHIPS_RESUME_MEMBERSHIP");
            resumeSubscription();
            afterResume();
          }}
        >
          {t("Resume Now to Enroll")}
        </Button>
      </Box>
    </Box>
  );
};

export const JoinMembershipModal = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const theme = useTheme();
  const { confettiCannon, fireConfetti } = useConfetti({
    spread: 180,
    elementCount: 100
  });
  const history = useHistory();
  const { sessionToken, currentUser, currentUserRefetch, isLoggedIn } =
    useSession();
  const { requiresMembershipForEnrollment } =
    useLoggedOutMembershipsExperiment();
  const { t } = useTranslation("client\\components\\payments\\Stripe");
  const location = useLocation();
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const query = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const redirectAfterPath = query.get("redirectAfterPath");
  const isEnrolling = location.pathname.includes("/classes/");
  const WELCOME30DISCOUNTCENTS = 3000;

  const recommendedPlan = React.useMemo(() => {
    return (
      SUBSCRIPTION_PLANS.find(plan => plan.numCredits === 130) ||
      SUBSCRIPTION_PLANS[0]
    );
  }, []);

  const utmSource = query.get("utm_source");
  const utmMedium = query.get("utm_medium");
  const utmCampaign = query.get("utm_campaign");

  const timeout = 20000;
  const pollingInterval = 3000;
  usePolling({
    shouldStart: state.pollForSubscription,
    onTick: currentUserRefetch,
    onTimeout: () => {
      dispatch({ type: "POLLING_TIMEOUT" });
    },
    timeout,
    interval: pollingInterval
  });

  const embeddedCheckoutOptions = React.useMemo(
    () => ({
      clientSecret: state.clientSecret,
      onComplete: async () => {
        try {
          const res = await fetch(`/check-session-status`, {
            method: "POST",
            body: JSON.stringify({ clientSecret: state.clientSecret }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionToken}`
            }
          });
          const { paymentStatus } = await res.json();
          if (paymentStatus === "paid") {
            dispatch({ type: "PROVISIONING_SUBSCRIPTION" });
          } else {
            dispatch({ type: "PAYMENT_FAILED" });
          }
        } catch {
          // Failed to verify payment status, marking as timeout to display message to user to contact support
          dispatch({ type: "POLLING_TIMEOUT" });
        }
      }
    }),
    [state.clientSecret, sessionToken]
  );

  const fetchClientSecret = React.useCallback(
    async (plan: string) => {
      const res = await fetch("/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          selection: plan.toLowerCase(),
          onSuccessPath: redirectAfterPath ?? homePath(),
          isEmbedded: true
        })
      });
      const secret = await res.text();
      dispatch({ type: "SET_CLIENT_SECRET", clientSecret: secret });
    },
    [redirectAfterPath, sessionToken]
  );

  const [joinMembershipModalLastShownAt, setJoinMembershipModalLastShownAt] =
    useLocalStorageState<string | null>("JoinMembershipModalLastShownAt", null);

  const onClose = React.useCallback(() => {
    setJoinMembershipModalLastShownAt(new Date().toISOString());
    dispatch({ type: "RESET" });
    const params = new URLSearchParams(location.search);
    // eslint-disable-next-line i18next/no-literal-string
    params.delete("joinMembershipModal");
    // eslint-disable-next-line i18next/no-literal-string
    params.delete("plan");

    history.replace({
      pathname: location.pathname,
      search: params.toString()
    });
  }, [
    history,
    location.pathname,
    location.search,
    dispatch,
    setJoinMembershipModalLastShownAt
  ]);

  React.useEffect(() => {
    if (
      currentUser &&
      requiresMembershipForEnrollment &&
      currentUser.hasSubscription &&
      !currentUser.canPayWithSubscriptionCredits
    ) {
      dispatch({ type: "RESUME_PLAN" });
    }
  }, [currentUser, requiresMembershipForEnrollment]);

  React.useEffect(() => {
    if (
      isLoggedIn &&
      requiresMembershipForEnrollment &&
      !currentUser?.hasSubscription &&
      query.get("joinMembershipModal") !== "true" &&
      !joinMembershipModalLastShownAt &&
      // skip auto-showing on the CDP
      !isEnrolling
    ) {
      query.set("joinMembershipModal", "true");
      const newUrl = `${location.pathname}?${query.toString()}`;
      navigate(newUrl, { replace: true });
    }
  }, [
    isLoggedIn,
    requiresMembershipForEnrollment,
    currentUser?.hasSubscription,
    query,
    joinMembershipModalLastShownAt,
    location.pathname,
    navigate,
    isEnrolling
  ]);

  React.useEffect(() => {
    const plan = query.get("plan");
    const shouldOpenModal = query.get("joinMembershipModal") === "true";
    if (shouldOpenModal && !state.isModalOpen) {
      dispatch({ type: "OPEN_MODAL" });
      trackEvent("JOIN_MEMBERSHIPS_MODAL_VIEW", {
        plan: recommendedPlan.title,
        userId: currentUser?.uid,
        utmCampaign,
        utmSource,
        utmMedium
      });
    }
    if (
      sessionToken &&
      shouldOpenModal &&
      ((plan &&
        SUBSCRIPTION_PLANS.some(
          p => p.title.toLowerCase() === plan.toLowerCase()
        )) ||
        state.selectedPlan)
    ) {
      dispatch({ type: "LOADING_CLIENT_SECRET" });
      fetchClientSecret((plan || state.selectedPlan) as string);
    }
  }, [
    sessionToken,
    query,
    state.selectedPlan,
    state.isModalOpen,
    fetchClientSecret,
    trackEvent,
    recommendedPlan.title,
    currentUser?.uid,
    utmCampaign,
    utmSource,
    utmMedium
  ]);

  React.useEffect(() => {
    if (
      currentUser?.hasSubscription &&
      currentUser?.canPayWithSubscriptionCredits &&
      state.status === STATUS.PROVISIONING_SUBSCRIPTION
    ) {
      dispatch({ type: "PROVISIONING_SUCCESS" });
      setJoinMembershipModalLastShownAt(new Date().toISOString());

      const planTitle = query.get("plan") || state.selectedPlan;

      const plan = SUBSCRIPTION_PLANS.find(
        p => p.title.toLowerCase() === planTitle?.toLowerCase()
      );

      if (plan) {
        trackAdEvent("New Buyer Acquired", {
          revenue: plan.centsPerMonth,
          total: plan.centsPerMonth,
          currency: "USD",
          isSubscription: true,
          subscriptionName: plan.title
        });

        trackAdEvent("New Buyer Membership Client", {
          plan: plan.title,
          revenue: plan.centsPerMonth,
          currency: "USD",
          userId: currentUser?.uid,
          utmCampaign,
          utmSource,
          utmMedium
        });
      }
    }

    if (state.status === STATUS.PROVISIONING_SUCCESS) {
      const confettiTimeOutId = setTimeout(() => {
        fireConfetti();
      }, 500);

      const resetTimeoutId = setTimeout(() => {
        if (isEnrolling && !redirectAfterPath) {
          onClose();
        } else {
          dispatch({ type: "RESET" });
          if (redirectAfterPath) {
            navigate(redirectAfterPath, { replace: true });
          } else {
            navigate(homePath(), { replace: true });
          }
        }
      }, 4000);

      return () => {
        clearTimeout(confettiTimeOutId);
        clearTimeout(resetTimeoutId);
      };
    }
    return;
  }, [
    currentUser,
    fireConfetti,
    navigate,
    redirectAfterPath,
    state.status,
    state.selectedPlan,
    query,
    trackAdEvent,
    utmCampaign,
    utmSource,
    utmMedium,
    isEnrolling,
    onClose,
    setJoinMembershipModalLastShownAt
  ]);

  React.useEffect(() => {
    if (
      currentUser &&
      currentUser.requiresMembershipForEnrollment &&
      currentUser.hasSubscription &&
      !currentUser.canPayWithSubscriptionCredits
    ) {
      dispatch({ type: "RESUME_PLAN" });
    }
  }, [currentUser]);

  const renderBody = () => {
    switch (state.status) {
      case STATUS.LOADING_CLIENT_SECRET:
        return (
          <Box sx={{ paddingRight: "2em" }}>
            <Loading />
          </Box>
        );

      case STATUS.EMBEDDED_CHECKOUT:
        return (
          <Box sx={{ width: "100%", paddingRight: "2em" }}>
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={embeddedCheckoutOptions}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </Box>
        );

      case STATUS.PROVISIONING_SUBSCRIPTION:
      case STATUS.POLLING_TIMEOUT:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {t`Processing your membership.`}
            </Typography>
            <Typography variant="body" sx={{ textAlign: "center" }}>
              {state.status === STATUS.PROVISIONING_SUBSCRIPTION
                ? t`Please do not close this browser until your transaction is complete.`
                : t`We are experiencing technical difficulties. Please contact support`}
            </Typography>
            {state.status === STATUS.PROVISIONING_SUBSCRIPTION && <Loading />}
          </Box>
        );
      case STATUS.PROVISIONING_SUCCESS:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32,
              position: "relative",
              overflow: "hidden",
              minHeight: 400
            }}
          >
            {confettiCannon && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)"
                }}
              >
                {confettiCannon}
              </Box>
            )}
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {t`Congrats! You are in!`}
            </Typography>
          </Box>
        );

      case STATUS.PAYMENT_FAILED:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32
            }}
          >
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>
              {t`Payment Failed`}
            </Typography>
            <Typography variant="body" sx={{ textAlign: "center" }}>
              {t`We were unable to process your payment. Please try again or use a different payment method.`}
            </Typography>
            <TrackedButton
              size="large"
              trackingName="join_memberships_payment_retry_button"
              variant="contained"
              onClick={() => dispatch({ type: "RESET" })}
            >
              {t`Try Again`}
            </TrackedButton>
          </Box>
        );

      case STATUS.RESUME_PLAN:
        return (
          <MembershipResumeContent
            afterResume={() => {
              dispatch({ type: "PROVISIONING_SUBSCRIPTION" });
            }}
          />
        );

      // STATUS.SELECT_PLAN
      default:
        if (isEnrolling) {
          return (
            <Box
              sx={{
                paddingRight: "2em"
              }}
            >
              <Typography variant="h3" sx={{ paddingRight: "2em" }}>
                {t("Enroll with a Plan")}
              </Typography>
              <Box
                sx={{
                  marginTop: 24,
                  paddingX: 16,
                  paddingY: 20,
                  border:
                    "1px solid var(--border-border-neutral-lighter, #E0E0E0)",
                  borderRadius: 16
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 8, fontSize: 18 }}>
                  {recommendedPlan.numCredits + " " + t("credits")}
                </Typography>
                <Box sx={{ marginBottom: 8 }}>
                  <Typography
                    as="span"
                    variant="display2"
                    sx={{
                      textDecoration: "line-through",
                      fontWeight: 400
                    }}
                  >
                    {`$${recommendedPlan.centsPerMonth / 100}`}
                  </Typography>{" "}
                  <Typography
                    as="span"
                    variant="display2"
                    sx={{
                      fontWeight: "bold"
                    }}
                  >
                    {`$${
                      recommendedPlan.centsPerMonth / 100 -
                      WELCOME30DISCOUNTCENTS / 100
                    }`}
                  </Typography>{" "}
                  <Typography as="span" variant="subtitle1">
                    {t("USD / month")}
                  </Typography>
                </Box>
                <Box>
                  <Chip
                    color="warning"
                    size="medium"
                    label={t("Save $30 your first month with code: WELCOME30")}
                    sx={{
                      height: "auto",
                      padding: "2px",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "clip",
                        lineHeight: "1.4",
                        padding: "4px 8px"
                      }
                    }}
                  />
                </Box>
                <Box
                  sx={{ marginTop: 30, display: "flex", flexDirection: "row" }}
                >
                  <Button
                    variant="contained"
                    disabled={!!Env.IS_READ_ONLY_MODE}
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.stopPropagation();
                      if (Env.IS_READ_ONLY_MODE) {
                        return;
                      }
                      trackEvent("JOIN_MEMBERSHIPS_PLAN_SELECTED", {
                        plan: recommendedPlan.title,
                        userId: currentUser?.uid,
                        utmCampaign,
                        utmSource,
                        utmMedium
                      });
                      dispatch({
                        type: "SET_PLAN",
                        selectedPlan: recommendedPlan.title
                      });
                      dispatch({ type: "LOADING_CLIENT_SECRET" });
                    }}
                  >
                    {t`Continue to Enroll`}
                  </Button>
                </Box>
                <Divider sx={{ marginY: 24 }} />
                {[
                  t("Access to 1000s of classes"),
                  t("Good for an average of 1 group class per week"),
                  t("Roll over up to 130 credits"),
                  <Box sx={{ textDecoration: "underline" }}>
                    <b>{t`Cancel risk-free within 14 days`}</b>
                  </Box>
                ].map(choice => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: 4,
                        alignItems: "center"
                      }}
                    >
                      <Box
                        sx={{
                          marginTop: "1px",
                          paddingRight: 4,
                          color: theme.palette.success.main,
                          fontSize: 14,
                          width: 16
                        }}
                      >
                        •
                      </Box>
                      <Box
                        sx={{ display: "flex", flex: 1, alignItems: "center" }}
                      >
                        <Typography variant={"body2"}>{choice}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }
        return (
          <Box flex sx={{ flexDirection: "column", gap: 24 }}>
            <Typography variant="h3" sx={{ paddingRight: "2em" }}>
              {t("Choose Your Membership Plan")}
            </Typography>
            <Box
              flex
              sx={{
                backgroundColor: "warning.300",
                borderRadius: 8,
                marginRight: 40,
                justifyContent: "space-between"
              }}
            >
              <Box
                sx={{
                  background: `url(${PurpleLinesTopLeft})`,
                  minWidth: 40,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  // dunno why the parent's BR isn't clipping this
                  borderTopLeftRadius: 8
                }}
              ></Box>
              <Box sx={{ paddingX: 16, paddingY: 12 }}>
                <Typography variant="subtitle2">
                  <Trans t={t}>
                    Get $30 off your first month only with code:{" "}
                    <b>WELCOME30</b> and cancel <b>risk-free</b> within 14 days!
                  </Trans>
                </Typography>
              </Box>
              <Box
                sx={{
                  background: `url(${OrangeFingerprintsBottomRight})`,
                  minWidth: 40,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "bottom",
                  // dunno why the parent's BR isn't clipping this
                  borderBottomRightRadius: 8
                }}
              ></Box>
            </Box>
            <Box
              sx={(theme: Theme) => ({
                paddingX: 24,
                display: "flex",
                flexDirection: "row",
                gap: 16,
                alignItems: "center",
                marginLeft: -24,
                marginRight: -24,
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  overflowX: "auto"
                }
              })}
            >
              {SUBSCRIPTION_PLANS.slice(0, 2).map(plan => (
                <MembershipOptionCard
                  key={plan.title}
                  isActive={
                    plan.title === state.selectedPlan ||
                    (!state.selectedPlan && plan.numCredits === 268)
                  }
                  isRecommended={plan.numCredits === 268}
                  plan={plan}
                  onSelect={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    if (Env.IS_READ_ONLY_MODE) {
                      return;
                    }
                    trackEvent("JOIN_MEMBERSHIPS_PLAN_SELECTED", {
                      plan: plan.title,
                      userId: currentUser?.uid,
                      utmCampaign,
                      utmSource,
                      utmMedium
                    });
                    dispatch({ type: "SET_PLAN", selectedPlan: plan.title });
                    dispatch({ type: "LOADING_CLIENT_SECRET" });
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingRight: 40,
                gap: 24,
                flexWrap: "wrap"
              }}
            >
              <TrackedButton
                variant="link"
                onClick={() => {
                  setJoinMembershipModalLastShownAt(new Date().toISOString());
                  dispatch({ type: "RESET" });
                  navigate(
                    searchPath(),
                    // Page hard-freezes when using soft-nav here... no idea why
                    {
                      hardNav: true,
                      replace: true
                    }
                  );
                }}
              >
                {t("Browse classes first")}
              </TrackedButton>

              <TrackedButton
                size="small"
                trackingName="join_memberships_modal_learn_more_button"
                variant="outlined"
                onClick={() => {
                  setJoinMembershipModalLastShownAt(new Date().toISOString());
                  dispatch({ type: "RESET" });
                  navigate(
                    joinMembershipPath({
                      ...(redirectAfterPath ? { redirectAfterPath } : undefined)
                    }),
                    // Page hard-freezes when using soft-nav here... no idea why
                    {
                      hardNav: true,
                      replace: true
                    }
                  );
                }}
              >
                {t`Learn More About Plans`}
              </TrackedButton>
            </Box>
          </Box>
        );
    }
  };

  return (
    <PageModal
      open={state.isModalOpen}
      onClose={onClose}
      sx={{ paddingRight: 0, overflow: "auto", position: "relative" }}
    >
      {renderBody()}
    </PageModal>
  );
};

const usePolling = ({
  shouldStart,
  onTick,
  onTimeout,
  timeout = 20000,
  interval = 3000
}: {
  shouldStart: boolean;
  onTick: () => void;
  onTimeout: () => void;
  timeout?: number;
  interval?: number;
}) => {
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (shouldStart) {
      pollingIntervalRef.current = setInterval(onTick, interval);
      pollingTimeoutRef.current = setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        onTimeout();
      }, timeout);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [shouldStart, onTick, onTimeout, timeout, interval]);
};
